/**
 * Single shared TanStack-Query key factory for the lead-side team
 * audit feed (`GET /api/teams/{team_id}/audit`).
 *
 * Mirrors `meAuditKey` (W2-A) + `teamConfigKey` (G-CC2): every query
 * key tuple for an authenticated read includes `accountId` so a member
 * who shares a browser session (dev-shim) can never reuse a previous
 * lead's cached rows. The `teamId` segment additionally namespaces the
 * cache so a lead who switches between two teams in their workspace
 * picker doesn't see the previous team's events while the new fetch is
 * in flight.
 *
 * The key is `as const` so TanStack's typed query-key inference flows
 * through; pass the same `(teamId, accountId)` tuple to every useQuery /
 * setQueryData / invalidateQueries call.
 */
export const teamAuditKey = (teamId: string, accountId: string | null) =>
  ["team-audit", teamId, accountId] as const;

export type TeamAuditKey = ReturnType<typeof teamAuditKey>;

/**
 * Prefix of every team-audit read — for a write that the feed records
 * (`cycle.published`, `schedule_republished`, …) to invalidate the ticker
 * and the full audit page without holding their `(teamId, accountId)`.
 * Without it the right panel's "Recent activity" kept its first read: right
 * after a republish it still led with "A schedule was published · 10 hours
 * ago" although the backend had recorded the republish (re-click r2-s5-29).
 */
export const TEAM_AUDIT_ROOT = ["team-audit"] as const;
